import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Tour } from '../mocks/tours';

export interface CartItem {
  tour: Tour;
  quantity: number;
  travelers: number;
  date: string;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (tour: Tour, travelers?: number, date?: string) => void;
  removeFromCart: (tourId: string) => void;
  updateQuantity: (tourId: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  isInCart: (tourId: string) => boolean;
}

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem('ankara-travelers-cart');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('ankara-travelers-cart', JSON.stringify(items));
  }, [items]);

  const addToCart = useCallback((tour: Tour, travelers = 1, date = '') => {
    setItems(prev => {
      const existing = prev.find(item => item.tour.id === tour.id);
      if (existing) {
        return prev.map(item =>
          item.tour.id === tour.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { tour, quantity: 1, travelers, date }];
    });
  }, []);

  const removeFromCart = useCallback((tourId: string) => {
    setItems(prev => prev.filter(item => item.tour.id !== tourId));
  }, []);

  const updateQuantity = useCallback((tourId: string, quantity: number) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(item => item.tour.id !== tourId));
      return;
    }
    setItems(prev =>
      prev.map(item =>
        item.tour.id === tourId ? { ...item, quantity } : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const totalPrice = items.reduce((sum, item) => {
    if (item.tour.currency === 'TL') {
      return sum + item.tour.price * item.quantity;
    }
    return sum;
  }, 0);

  const isInCart = useCallback(
    (tourId: string) => items.some(item => item.tour.id === tourId),
    [items]
  );

  return (
    <CartContext.Provider value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, totalItems, totalPrice, isInCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
